import React, { FC, useEffect, useState } from 'react'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import MailIcon from '@material-ui/icons/MailOutline'
import { makeStyles, Theme, Container, Grid, Typography, Button, Box } from '@material-ui/core'
import classnames from 'classnames'
// Components
import { Video } from 'components/shared'

// Types
import { ISession } from 'helpers/sessions'
import { GameFlowStepsConfig } from 'helpers/steps'
import { EventStages, GameFlowSteps } from 'types'

interface SessionProps {
  session: ISession
  setScene: (sceneTo: GameFlowSteps) => void
  eventStage?: EventStages
}

export const Session: FC<SessionProps> = ({ session, setScene, eventStage }) => {
  const classes = useStyles()
  // eslint-disable-next-line
  const [loading, setLoading] = useState<boolean>()
  // eslint-disable-next-line
  const [showJoinSession, setShowJoinSession] = useState<boolean>(checkEventStage())
  const [renderSession, setRenderSession] = useState<boolean>(false)

  useEffect(() => {
    const timeoutTime = GameFlowStepsConfig[GameFlowSteps.Session].animation.time

    setTimeout(() => {
      setRenderSession(true)
    }, timeoutTime)
  }, [])

  useEffect(() => {
    setShowJoinSession(checkEventStage())
    // eslint-disable-next-line
  }, [eventStage])

  function checkEventStage() {
    return Boolean(eventStage && ![EventStages.POSTSHOW].includes(eventStage))
  }

  const goBackToSessions = () => setScene(GameFlowSteps.BackToSessions)

  const actionClicked = () => {
    window.postMessage(JSON.stringify({ command: 'connect', param: session.side.header }), '*')
  }

  return (
    <>
      {renderSession && (
        <Container className={`${classes.root} ${classes.transition}`} component='main' maxWidth={false}>
          <Grid container justify={'space-between'} spacing={2}>
            <Grid item md={7} xs={4} className={classes.contentContainer}>
              <Video videoSrc={session.videoSrc} posterSrc={session.image}></Video>
              <Button
                className={classnames([classes.joinRowButtons, classes.mobileBackButton])}
                startIcon={<ArrowBackIcon />}
                onClick={() => goBackToSessions()}
              >
                Back
              </Button>
            </Grid>
            <Grid container item direction='column' alignItems='flex-start' justify='center' md={5} xs={8}>
              <Typography className={classes.marginBottom} component='h5' variant='h5' color='textPrimary' gutterBottom>
                Breakout Session
              </Typography>
              <Typography className={classes.marginBottom} component='h1' variant='h2' color='textPrimary' gutterBottom>
                {session.side.header}
              </Typography>
              {session.side.speakers &&
                session.side.speakers.map(speaker => {
                  return (
                    <Typography className={classes.marginBottom} component='p' color='textPrimary'>
                      {speaker}
                    </Typography>
                  )
                })}
              <Typography component='p' variant='body2' color='textPrimary' gutterBottom>
                {session.side.body}
              </Typography>
              <Box display='flex'>
                <Button
                  className={classnames([classes.joinRowButtons, classes.backButton])}
                  startIcon={<ArrowBackIcon />}
                  onClick={() => {
                    setScene(GameFlowSteps.BackToSessions)
                  }}
                >
                  Back
                </Button>
                <Button
                  className={classnames([classes.joinRowButtons, classes.backButton, classes.sendButton])}
                  startIcon={<MailIcon />}
                  onClick={actionClicked}
                >
                  Send a message
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      )}
    </>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: '3rem',
    height: 'calc(100% - 115px)',
    width: '100vw',
    top: '115px',
    position: 'absolute',
    backgroundColor: 'transparent',
    color: '#000',
    overflow: 'auto',
    [theme.breakpoints.down('sm')]: {
      height: 'auto'
    },
    [`${theme.breakpoints.down('sm')}, screen and (max-height: 740px)`]: {
      top: '75px',
      paddingTop: '1rem'
      // height: 'calc(100% - 75px)'
    },
    [theme.breakpoints.up('md')]: {
      '& .MuiGrid-item': {
        padding: '3rem'
      }
    }
  },
  preEventVidPlaceholderImg: {
    width: '100%'
  },
  contentContainer: {
    margin: 'auto',

    [`${theme.breakpoints.down('sm')}, screen and (max-height: 740px)`]: {
      margin: 0
    }
  },
  detailsContainer: {
    [`${theme.breakpoints.down('sm')}, screen and (max-height: 540px)`]: {
      justifyContent: 'flex-start'
    }
  },
  breakout: {
    marginBottom: 17,

    [`${theme.breakpoints.down('sm')}, screen and (max-height: 540px)`]: {
      display: 'none'
    }
  },
  header: {
    [`${theme.breakpoints.down('sm')}, screen and (max-height: 540px)`]: {
      fontSize: 16,
      marginBottom: '10px'
    }
  },
  bodyText: {
    [`${theme.breakpoints.down('sm')}, screen and (max-height: 540px)`]: {
      marginBottom: 14,
      overflowY: 'scroll'
    }
  },
  marginBottom: {
    marginBottom: 17
  },
  joinRowButtons: {
    minHeight: '36px',
    height: 'initial',
    marginTop: '14px',

    [`${theme.breakpoints.down('sm')}, screen and (max-height: 740px)`]: {
      marginTop: 0
    }
  },
  backButton: {
    marginTop: '6px',

    [`${theme.breakpoints.down('sm')}, screen and (max-height: 740px)`]: {
      display: 'none'
    }
  },
  sendButton: {
    marginLeft: '16px'
  },
  mobileBackButton: {
    marginTop: '6px',
    marginLeft: 0,
    display: 'none',

    [`${theme.breakpoints.down('sm')}, screen and (max-height: 740px)`]: {
      display: 'inline-flex'
    }
  },
  availableSeatsMessage: {
    fontSize: '14px',
    margin: '22px 30px 36px 30px'
  },
  sessionSplashImage: {
    position: 'relative',
    width: '100%',
    height: 'auto'
  },
  transition: {
    opacity: 1,
    animationName: '$fadeInOpacity',
    animationIterationCount: 1,
    animationTimingFunction: 'ease-in',
    animationDuration: '0.75s'
  },
  '@keyframes fadeInOpacity': {
    '0%': {
      opacity: 0
    },
    '100%': {
      opacity: 1
    }
  }
}))
